import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormError } from '@/components/FormError'
import { api } from '@/lib/api'
import { circleStyle } from '@/lib/circleColor'
import type { Family } from '@/lib/families'
import { cn } from '@/lib/utils'
import { CIRCLE_COLOR_LABELS, CIRCLE_COLORS, type CircleColor } from '../../shared/circleColors'

// The circle's colour, in Circle settings: the ring everyone's picture wears while they're in this circle. Anyone in
// the circle can change it, as with its name. Saved as soon as one is picked.
export function CircleColorPicker({ family, name = 'circle-color' }: { family: Family; name?: string }) {
  const queryClient = useQueryClient()
  const change = useMutation({
    mutationFn: (color: CircleColor) => api(`/families/${family.id}`, { method: 'PATCH', body: { color } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['family', family.id] })
      await queryClient.invalidateQueries({ queryKey: ['families'] })
      await queryClient.invalidateQueries({ queryKey: ['circles'] })
    },
  })
  const shown = change.isPending && change.variables ? change.variables : family.color
  return (
    <fieldset className="grid gap-3">
      <legend className="text-sm font-medium">Colour</legend>
      <p className="-mt-1 text-sub text-muted-foreground">
        The ring around everyone’s picture while they’re in this circle.
      </p>
      <div className="grid grid-cols-4 gap-x-2 gap-y-4 sm:flex sm:flex-wrap sm:gap-5">
        {CIRCLE_COLORS.map((color) => (
          <label
            key={color}
            className="group flex cursor-pointer flex-col items-center gap-1.5 text-sub text-muted-foreground has-checked:font-semibold has-checked:text-foreground"
          >
            <input
              type="radio"
              name={name}
              value={color}
              checked={shown === color}
              onChange={() => change.mutate(color)}
              className="peer sr-only"
            />
            <span
              aria-hidden="true"
              style={circleStyle(color)}
              className={cn(
                'block size-10 rounded-full bg-(--circle) transition-shadow',
                'peer-checked:ring-2 peer-checked:ring-foreground peer-checked:ring-offset-3 peer-checked:ring-offset-background',
                'peer-focus-visible:ring-[3px] peer-focus-visible:ring-ring/50 peer-focus-visible:ring-offset-3 peer-focus-visible:ring-offset-background',
              )}
            />
            {CIRCLE_COLOR_LABELS[color]}
          </label>
        ))}
      </div>
      <FormError error={change.error} />
    </fieldset>
  )
}
